import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon, FolderIcon } from '@heroicons/react/20/solid';
import moment from 'moment';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import DataTable from '../../components/DataTable';
import { AvatarGenerated } from '../../components/Avatar';
import Badge from '../../components/Badge';
import { TAG_COLOR_OPTIONS } from '../../components/helpers/constants';
import {
	capitalizeFirstLetter,
	classNames,
	createDataTree,
} from '../../components/helpers/functions';

import { getModuleFields } from '../../api/modules';
import { getAssets, getLocation, getLocations } from '../../api';

import usePrevious from '../../hooks/usePrevious';
import useSearchParams from '../../hooks/useSearchParams';
import useAsync from '../../hooks/useAsync';

import LocationList from './LocationList';
import AccordianList from '../../components/Accordian';
import useSearchQuery from '../../hooks/useSearchQuery';
import { TemplateReactSelect } from '../../components/Attributes/Attributes_old';
import useSettings from '../../hooks/useSettings';

const ASSETS_MODULE_ID = process.env.REACT_APP_MODULE_ID_ASSETS;

const getMembers = (members) => {
	let nodes = [];

	return members
		.map((m) => {
			if (m.nodes && m.nodes.length) {
				nodes = [...nodes, ...m.nodes];
			}
			return m;
		})
		.concat(nodes.length ? getMembers(nodes) : nodes);
};

const AllDashboard = () => {
	const navigate = useNavigate();

	const {
		user: userSettings,
		get: getSettings,
		set: updateSettings,
	} = useSettings();

	const paths = history?.location?.pathname?.split('/');
	const action = paths?.length >= 3 ? paths[2] : null;

	const {
		locationId: locationFilter,
		categoryId: categoryPathFilter,
		manufacturerId: manufacturerPathFilter,
		modelId: modelPathFilter,
	} = useParams();

	const {
		vendorId: vendorFilter,
		categoryId: categoryFilter,
		manufacturerId: manufacturerFilter,
		modelId: modelFilter,
		query: queryFilter,
		page: pageFilter,
		pageSize: pageSizeFilter,
		sortBy: sortByFilter,
		sortOrder: sortOrderFilter,
		//issues: issuesFilter,
		locationId: locationSearchFilter,
		tagIds: tagsSearchFilter,
	} = useSearchParams();

	const defaultFilters = {
		tagIds: tagsSearchFilter?.split(',') || null,
		categoryId: categoryFilter || categoryPathFilter,
		manufacturerId: manufacturerFilter || manufacturerPathFilter,
		modelId: modelFilter || modelPathFilter,
		vendorId: vendorFilter,
		page: pageFilter || 1,
		pageSize: pageSizeFilter || action !== 'all' ? 10 : null,
		query: queryFilter,
		sortBy: sortByFilter,
		sortOrder: sortOrderFilter,
		locationId:
			locationFilter || locationSearchFilter === 'no-location'
				? 'unassigned'
				: locationSearchFilter,
		withIssues: action === 'issues',
	};

	const {
		value: items,
		status,
		error,
		execute: refreshAssets,
	} = useAsync(getAssets, {
		silent: true,
		defaultPayload: defaultFilters,
		immediate: false,
	});

	/*const { execute: onExportAssets } = useAsync(getAssets, {
		silent: true,
	});*/

	const { value: locations } = useAsync(getLocations);
	const {
		value: location,
		execute: onRefreshLocation,
		resetValue,
	} = useAsync(getLocation, { immediate: false });
	const { value: moduleFields } = useAsync(getModuleFields, {
		defaultPayload: { id: ASSETS_MODULE_ID },
	});

	useEffect(() => {
		if (locationFilter) {
			if (locationFilter === 'no-location') {
				refreshAssets({ locationId: 'unassigned' });
				resetValue();
			} else {
				refreshAssets({ locationId: locationFilter });
				onRefreshLocation({ id: locationFilter });
			}
		}
	}, [locationFilter]);

	const prevQueryFilter = usePrevious(queryFilter);
	useEffect(() => {
		if (queryFilter && prevQueryFilter) {
			const delayDebounceFn = setTimeout(
				() => {
					refreshAssets(defaultFilters);
				},
				queryFilter ? 600 : 0,
			);

			return () => clearTimeout(delayDebounceFn);
		}
	}, [queryFilter]);

	useEffect(() => {
		refreshAssets(defaultFilters);
	}, [
		defaultFilters?.vendorId,
		defaultFilters?.categoryId,
		defaultFilters?.manufacturerId,
		defaultFilters?.modelId,
		defaultFilters?.page,
		defaultFilters?.sortBy,
		defaultFilters?.sortOrder,
		locationSearchFilter,
		categoryPathFilter,
		manufacturerPathFilter,
		modelPathFilter,
		tagsSearchFilter,
	]);

	const locationName = location?.name;

	const locationChildren = locations?.rows?.filter(
		(location) => location.parent_id === locationFilter,
	);

	const locationParentId = locations?.rows?.find(
		(location) => location.id === locationFilter,
	)?.parent_id;

	// Set Location Tree for initial page render
	const locationDataTree = createDataTree(locations?.rows || [], 'nodes');
	locationDataTree.sort((a, b) => b.isDefault - a.isDefault);
	locationDataTree.push({
		name: 'No location set',
		assets: locations?.undefinedLocations,
		id: 'no-location',
	});
	const locationDataTreeWithCount = locationDataTree.map((location) => {
		const members = getMembers([location])?.reduce(
			(g, o) => g + o?.assets?.length,
			0,
		);

		return { assetCount: members, ...location };
	});

	const counts = {};

	const tags = items?.uniqueTags?.filter((x) =>
		defaultFilters?.tagIds?.includes(x?.id),
	);

	// Tags Workaround
	const updatedItems = items?.rows?.filter((item) =>
		tags?.every((v) => item?.tags?.map((tag) => tag?.id)?.includes(v?.id)),
	);

	const activeBanner = location?.health
		? {
				outage: {
					message: (
						<>
							<span className="font-medium">Critical: </span>This location is
							experiencing an outage
						</>
					),
					status: 'error',
				},
				locationOutage: {
					message: (
						<>
							<span className="font-medium">Critical: </span>This location is
							experiencing a location wide outage. Please avoid using any system
							in this location until the issue has been resolved
						</>
					),
					status: 'error',
				},
				partialOutage: {
					message: (
						<>
							<span className="font-medium">Warning: </span>This location is
							experiencing a partial outage. Please check the affected systems
							before using
						</>
					),
					status: 'warning',
				},
				completeOutage: {
					message: (
						<>
							<span className="font-medium">Critical: </span>This location is
							experiencing a complete outage. Please avoid using this system
							until we have resolved the issue
						</>
					),
					status: 'error',
				},
				disruption: {
					message: (
						<>
							<span className="font-medium">Caution: </span>This location is
							experiencing a disruption. Please proceed with caution
						</>
					),
					status: 'caution',
				},
				locationDisruption: {
					message: (
						<>
							<span className="font-medium">Caution: </span>This location is
							experiencing a disruption. Please proceed with caution
						</>
					),
					status: 'caution',
				},
				partialDisruption: {
					message: (
						<>
							<span className="font-medium">Caution: </span>This location is
							experiencing a partial disruption. Please verify that the system
							you are trying to use is not affected
						</>
					),
					status: 'caution',
				},
				completeDisruption: {
					message: (
						<>
							<span className="font-medium">Warning: </span>This location is
							experiencing a complete disruption. Please be cautious when using
							this system until the issues are resolved
						</>
					),
					status: 'warning',
				},
			}[location?.health]
		: null;

	const hideElements =
		locationFilter || action === 'all' || action === 'issues';

	const customColumns = [
		{
			header: 'Next PM',
			columnName: 'Next PM',
			accessorKey: 'nextPM',
			cell: (info) => {
				const row = info?.row?.original;
				const date = moment(row?.nextPM?.date);
				const isOverdue = moment().isAfter(date);

				return row?.nextPM?.date ? (
					isOverdue ? (
						<Badge isBeta status={'error'} size="xxs">{`Overdue: ${date?.format(
							'MM/DD/YYYY',
						)}`}</Badge>
					) : (
						date.format('MM/DD/YYYY')
					)
				) : (
					''
				);
			},
			column: {
				removable: true,
			},
		},
	];

	return (
		<MainContent
			banners={activeBanner ? [{ ...activeBanner, size: 'xs' }] : []}
			maxWidth={getSettings('assetsTableExpanded') ? 'w-full' : undefined}
		>
			<PageHeading
				actions={[
					{
						name: 'Add Assets',
						onClick: () =>
							navigate(
								`/assets/add${
									locationFilter ? `?location=${locationFilter}` : ''
								}`,
							),
					},
				]}
				divider={false}
				onBack={
					hideElements
						? () =>
								navigate(
									locationParentId
										? `/assets/location/${locationParentId}`
										: '/assets',
								)
						: undefined
				}
			>
				{locationFilter
					? locationName || 'No location'
					: action === 'issues'
						? 'Assets with issues'
						: action === 'all'
							? 'All assets'
							: 'Assets'}
			</PageHeading>

			{!hideElements ? (
				<div className="mb-10">
					<dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
						<div
							className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-12 shadow sm:px-6 sm:pt-6 cursor-pointer"
							onClick={() => navigate(`/assets/all`)}
						>
							<dt>
								<div className="absolute rounded-md bg-gray-300 p-2">
									<FolderIcon
										className="h-6 w-6 text-white"
										aria-hidden="true"
									/>
								</div>
								<p className="ml-16 truncate text-sm font-medium text-gray-500">
									Total Assets
								</p>
							</dt>
							<dd className="flex items-baseline pb-4">
								<p className="ml-16 text-2xl font-semibold text-gray-900">
									{items?.total || 0}
								</p>
								<div className="absolute inset-x-0 bottom-0 bg-gray-50 px-2 py-4 sm:px-4">
									<div className="text-xs">
										<div className="font-medium text-gray-600 hover:text-gray-500 cursor-pointer">
											View All
										</div>
									</div>
								</div>
							</dd>
						</div>

						<div
							className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-12 shadow sm:px-6 sm:pt-6 cursor-pointer"
							onClick={() => navigate(`/assets/issues`)}
						>
							<dt>
								<div
									className={classNames(
										'absolute rounded-md p-3',
										items?.assetsWithIssues ? ' bg-red-500' : ' bg-gray-300',
									)}
								>
									<ExclamationTriangleIcon
										className="h-6 w-6 text-white"
										aria-hidden="true"
									/>
								</div>
								<p className="ml-16 truncate text-sm font-medium text-gray-500">
									With Issues
								</p>
							</dt>
							<dd className="flex items-baseline pb-4">
								<p className="ml-16 text-2xl font-semibold text-gray-900">
									{items?.assetsWithIssues || 0}
								</p>
								<div className="absolute inset-x-0 bottom-0 bg-gray-50 px-2 py-4 sm:px-4">
									<div className="text-xs">
										<div className="font-medium text-gray-600 hover:text-gray-500 cursor-pointer">
											View All with Issues
										</div>
									</div>
								</div>
							</dd>
						</div>
					</dl>
				</div>
			) : null}

			{status === 'pending' ? (
				<LoadingSkeleton />
			) : (
				<>
					<div className="grid grid-cols-12 gap-x-8">
						<div className="col-span-12">
							<DataTable
								pinColumn={['manufacturer']}
								columns={[
									{
										header: 'Asset',
										accessorKey: 'manufacturer',
										enablePinning: true,
										minSize: 180,
										cell: (info) => {
											const asset = info?.row?.original;
											const manufacturer =
												asset?.manufacturer?.name || asset?.manufacturerName;
											const model = asset?.model?.name || asset?.modelName;
											return `${manufacturer} ${model}`;
											/*return (
												<div>
													{/*<div style={{ float: 'left', marginRight: '1rem' }}>
														<AvatarGenerated
															size="xs"
															type="asset"
															id={asset?.id}
														/>
											</div>
													<div className="ml-4 whitespace-nowrap pr-4">
														<div className="font-medium"></div>
													</div>
												</div>
											);*/
										},
										enableFiltering: true,
										filter: [
											{
												key: 'manufacturerId',
												label: 'Manufacturer',
												placeholder: 'Select a manufacturer',
												options: items?.uniqueManufacturers || [],
											},
											{
												key: 'modelId',
												label: 'Model',
												placeholder: 'Select a model',
												options: items?.uniqueModels || [],
											},
										],
									},
									{
										header: 'Serial Number',
										accessorKey: 'serialNumber',
										cell: (info) => {
											return info?.getValue() || '';
										},
										enableSorting: true,
									},
									{
										header: 'Category',
										accessorKey: 'category.name',
										id: 'category',
										cell: (info) => {
											return info?.getValue() || '';
										},
										enableFiltering: true,
										filter: {
											key: 'categoryId',
											label: 'Category',
											placeholder: 'Select a category',
											options: items?.uniqueCategories || [],
										},
										sortDescFirst: false,
										column: {
											removable: true,
										},
									},

									{
										header: 'Tags',
										accessorKey: 'tags',
										id: 'tags',
										cell: (info) => {
											//return null; //info?.getValue() || '';
											return (
												info?.getValue()?.map((tag) => (
													<Badge
														isBeta
														size="xxs"
														className={classNames(
															TAG_COLOR_OPTIONS.find(
																(color) => color?.key === tag?.color,
															)?.value,
															'mr-1.5 my-1',
														)}
													>
														{tag?.name}
													</Badge>
												)) || null
											);
										},
										enableFiltering: true,
										filter: {
											key: 'tagIds',
											label: 'Tags',
											placeholder: 'Select a tag',
											options: items?.uniqueTags || [],
											component: () => {
												return (
													<div className="flex flex-col">
														<div>
															<TemplateReactSelect
																className="w-64"
																placeholder={'Select a tag'}
																options={
																	items?.uniqueTags
																		?.filter(
																			(tag) =>
																				!defaultFilters?.tagIds?.includes(
																					tag?.id,
																				),
																		)
																		?.filter(Boolean)
																		.map((vendor) => ({
																			label: vendor?.name,
																			value: vendor?.id,
																		}))
																		.sort((a, b) =>
																			a?.label?.localeCompare(b?.label),
																		) || []
																}
																onChange={(index, option) => {
																	useSearchQuery.add(
																		'tagIds',
																		[
																			...tags?.map((tag) => tag.id),
																			option?.value,
																		].join(','),
																	);
																}}
															/>
														</div>
														{tags?.map((tag) => (
															<Badge
																isBeta
																className={classNames(
																	TAG_COLOR_OPTIONS.find(
																		(color) => color?.key === tag?.color,
																	)?.value,
																	'mr-1.5 mt-1.5 justify-between',
																)}
																size="xxs"
															>
																{tag?.name}
																<XMarkIcon
																	className="w-5 h-5 text-inherit-400  p-1 cursor-pointer"
																	onClick={() => {
																		const newValues = [
																			...defaultFilters?.tagIds?.filter(
																				(v) => v !== tag?.id,
																			),
																		];

																		useSearchQuery.remove('tagIds');

																		useSearchQuery.add(
																			'tagIds',
																			newValues.join(','),
																		);
																	}}
																/>
															</Badge>
														))}
													</div>
												);
											},
										},
										sortDescFirst: false,
										column: {
											removable: true,
										},
									},
									{
										header: 'Status',
										accessorKey: 'status',
										cell: (info) => {
											const badgeData = {
												operational: { label: 'Operational', value: 'success' },
												maintenance: { label: 'Maintenance', value: 'info' },
												caution: { label: 'Caution', value: 'caution' },
												down: { label: 'Down', value: 'error' },
												offline: { label: 'Offline', value: 'default' },
											}[info?.row?.original?.health || 'operational'];

											return info?.row?.original?.health ? (
												<Badge isBeta status={badgeData?.value} size="xxs">
													{badgeData?.label}
												</Badge>
											) : null;
										},
										enableSorting: false,
										column: {
											removable: true,
										},
									},
									{
										header: 'Scipods Tag',
										accessorKey: 'tag',
										cell: (info) => {
											return info?.getValue() || '';
										},
										enableSorting: false,
										column: {
											removable: true,
										},
									},
									{
										header: 'Location',
										accessorKey: 'location.name',
										id: 'location',
										cell: (info) => {
											return info?.getValue() || '';
										},
										enableSorting: true,
										sortDescFirst: false,
										enableFiltering: true,
										filter: {
											key: 'locationId',
											label: 'Location',
											placeholder: 'Select a location',
											options: items?.uniqueLocations || [],
										},
										column: {
											removable: true,
										},
									},
									...customColumns,
									...(moduleFields
										? moduleFields
												?.filter(
													(attr) =>
														[
															'manufacturer',
															'model',
															'serialNumber',
															'location',
															'category',
															'tags',
														].indexOf(attr?.key) < 0,
												)
												.map((attr) => {
													return {
														header: capitalizeFirstLetter(attr?.label),
														accessorKey: attr?.id,
														cell: (info) => {
															const attribute =
																info.row?.original?.custom_attributes?.find(
																	(o) => o.attribute_field_id === attr?.id,
																);
															return attribute?.value || '';
														},
														enableSorting: false,
														column: {
															removable: true,
														},
													};
												})
										: []),
								]}
								data={
									items?.rows?.length
										? {
												...items,
												total: items?.paginationTotal,
												//rows: updatedItems,
											}
										: { rows: [] }
								}
								onRow={(item) =>
									navigate(`/assets/${item?.original?.id}/overview`)
								}
								rowActions={{
									name: 'View',
									onClick: (row) =>
										navigate(`/assets/${row?.original?.id}/overview`),
								}}
								usePagination={action !== 'all'}
								columnManagementKey="assetsMainColumns"
								//onExport={onExportAssets}
								exportFileName={`assets-${moment().format('MM-DD-YYYY')}`}
								useExport={false}
								hasCheckboxes={false}
								settingsMenuItems={[
									{
										name: getSettings('assetsTableExpanded')
											? 'Collapse Table'
											: 'Expand Table',
										onClick: () =>
											updateSettings(
												'user',
												'assetsTableExpanded',
												!getSettings('assetsTableExpanded'),
											),
									},
								]}
							/>
						</div>
					</div>
				</>
			)}

			{locationChildren?.length ? (
				<>
					<h3 class="text-sm font-semibold leading-6 text-gray-700 flex align-center tracking-tight mb-4">
						Locations
					</h3>
					<LocationList locations={locationChildren} />
				</>
			) : null}

			{/*<DangerModal
        title="Are you sure you want to archive this asset?"
        open={isArchiveOpen}
        confirmText="Archive"
        onClose={() => openArchive(false)}
        onConfirm={() =>
            onArchiveItem({ id: isArchiveOpen?.id }, () => {
                openArchive(false);
                refreshAssets();
            })
        }
    />*/}
		</MainContent>
	);
};

export default AllDashboard;
